import type { CensusBasis, CensusView } from '../types.js'

/** Whose census the Trends chart draws: the facility (every unit summed) or one unit by id. */
export type TrendSeries = 'facility' | string

/** One day of the chart: the count, and whether staff recorded it or it is the forecast. */
export interface TrendDay {
  date: string
  /** Null where a unit has no count and nothing to forecast from. */
  census: number | null
  /** `recorded` only when every unit summed was recorded that day. */
  kind: 'recorded' | 'forecast'
  /** The forecast's basis for a single unit; for the facility, the weakest of its units'. */
  basis: CensusBasis | null
}

export interface TrendStats {
  average: number | null
  low: TrendDay | null
  high: TrendDay | null
  /** Days under the planned census, of the days with a count. */
  underPlanned: number | null
  daysCounted: number
  /** The month's patient days: the sum of its counts. */
  patientDays: number
}

export interface CensusTrend {
  days: TrendDay[]
  /** The planned census the month's shifts were published for; null when a unit summed has none. */
  planned: number | null
  stats: TrendStats
}

const BASIS_ORDER: readonly CensusBasis[] = ['recorded', 'weekday_average', 'planned']

/** Every date from `from` to `to`, `YYYY-MM-DD`. */
export function datesBetween(from: string, to: string): string[] {
  const dates: string[] = []
  for (let day = new Date(`${from}T12:00:00Z`); day.toISOString().slice(0, 10) <= to; day.setUTCDate(day.getUTCDate() + 1)) {
    dates.push(day.toISOString().slice(0, 10))
  }
  return dates
}

/** The first and last date of a month, `YYYY-MM`. */
export function monthBounds(period: string): { from: string; to: string } {
  const [year, month] = period.split('-').map(Number)
  const last = new Date(Date.UTC(year ?? 1970, month ?? 1, 0)).getUTCDate()
  return { from: `${period}-01`, to: `${period}-${String(last).padStart(2, '0')}` }
}

function statsOf(days: readonly TrendDay[], planned: number | null): TrendStats {
  const counted = days.filter((day): day is TrendDay & { census: number } => day.census !== null)
  const patientDays = counted.reduce((sum, day) => sum + day.census, 0)
  return {
    average: counted.length > 0 ? Math.round((patientDays / counted.length) * 10) / 10 : null,
    low: counted.reduce<TrendDay | null>((low, day) => (low === null || (low.census ?? Infinity) > day.census ? day : low), null),
    high: counted.reduce<TrendDay | null>((high, day) => (high === null || (high.census ?? -Infinity) < day.census ? day : high), null),
    underPlanned: planned === null ? null : counted.filter((day) => day.census < planned).length,
    daysCounted: counted.length,
    patientDays,
  }
}

/**
 * The month's census by day for the Trends chart (Month tab, 26 October 2026), from the census read: one unit's
 * figure each day, or the facility's - every unit summed, recorded only where every unit was, and no count for a day
 * any unit has none, so a total never silently leaves a unit out.
 */
export function censusTrend(view: CensusView, series: TrendSeries, from: string, to: string): CensusTrend {
  const units = series === 'facility' ? view.units : view.units.filter((unit) => unit.id === series)
  const unitIds = new Set(units.map((unit) => unit.id))
  const days = datesBetween(from, to).map((date): TrendDay => {
    const rows = view.rows.filter((row) => row.date === date && unitIds.has(row.unitId))
    if (units.length === 0 || rows.length < units.length || rows.some((row) => row.census === null)) {
      return { date, census: null, kind: 'forecast', basis: null }
    }
    const figures = rows.flatMap((row) => (row.census === null ? [] : [row]))
    const basis = BASIS_ORDER[Math.max(...figures.map((figure) => BASIS_ORDER.indexOf(figure.basis)))] ?? null
    return { date, census: figures.reduce((sum, figure) => sum + figure.census, 0), kind: basis === 'recorded' ? 'recorded' : 'forecast', basis }
  })
  const planned = units.length > 0 && units.every((unit) => unit.plannedCensus !== null) ? units.reduce((sum, unit) => sum + (unit.plannedCensus ?? 0), 0) : null
  return { days, planned, stats: statsOf(days, planned) }
}

/** The value axis: whole steps of 5 (or 2 for a small unit) around the counts and the planned line, with room above and below. */
export function trendScale(values: readonly number[]): { min: number; max: number; ticks: number[] } {
  if (values.length === 0) {
    return { min: 0, max: 10, ticks: [0, 5, 10] }
  }
  const low = Math.min(...values)
  const high = Math.max(...values)
  const step = high - low > 12 || high > 60 ? 5 : 2
  const min = Math.max(0, Math.floor((low - step) / step) * step)
  const max = Math.ceil((high + step) / step) * step
  const ticks: number[] = []
  for (let tick = min; tick <= max; tick += step) {
    ticks.push(tick)
  }
  return { min, max, ticks }
}
